import React, { useState, useEffect } from 'react';
import { 
  FileText, Search, Filter, Download, ChevronDown, ChevronUp, 
  BarChart3, Users, FolderOpen, Calculator, Settings, Play,
  X, Calendar, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { informesAPI } from '../api';
import { toast } from '../hooks/use-toast';

const Informes = () => {
  const [informes, setInformes] = useState([]);
  const [filtros, setFiltros] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterSubtipo, setFilterSubtipo] = useState('all');
  
  // Estado para el diálogo de configuración
  const [selectedInforme, setSelectedInforme] = useState(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [configParams, setConfigParams] = useState({
    cliente_id: '',
    responsable_id: '',
    fecha_inicio: '',
    fecha_fin: '',
    estado: ''
  });
  
  // Estado para resultados
  const [resultados, setResultados] = useState(null);
  const [isResultsDialogOpen, setIsResultsDialogOpen] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  const tipoIcons = {
    'Actuaciones': FileText,
    'Expedientes': FolderOpen,
    'Contabilidad': Calculator,
    'Personalizado': Settings
  };

  const tipoColors = {
    'Actuaciones': 'bg-blue-100 text-blue-700',
    'Expedientes': 'bg-green-100 text-green-700',
    'Contabilidad': 'bg-purple-100 text-purple-700',
    'Personalizado': 'bg-orange-100 text-orange-700'
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [informesRes, filtrosRes] = await Promise.all([
        informesAPI.getTipos(),
        informesAPI.getFiltros()
      ]);
      setInformes(informesRes.data);
      setFiltros(filtrosRes.data);
    } catch (error) {
      console.error('Error cargando informes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los informes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredInformes = informes.filter(inf => {
    const matchSearch = inf.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       inf.descripcion.toLowerCase().includes(searchTerm.toLowerCase());
    const matchTipo = filterTipo === 'all' || inf.tipo === filterTipo;
    const matchSubtipo = filterSubtipo === 'all' || inf.subtipo === filterSubtipo;
    return matchSearch && matchTipo && matchSubtipo;
  });

  const openConfigDialog = (informe) => {
    setSelectedInforme(informe);
    setConfigParams({
      cliente_id: '',
      responsable_id: '',
      fecha_inicio: '',
      fecha_fin: '',
      estado: ''
    });
    setIsConfigDialogOpen(true);
  };

  const handleGenerateReport = async () => {
    if (!selectedInforme) return;
    
    setGeneratingReport(true);
    try {
      const params = {};
      if (configParams.cliente_id) params.cliente_id = configParams.cliente_id;
      if (configParams.responsable_id) params.responsable_id = configParams.responsable_id;
      if (configParams.fecha_inicio) params.fecha_inicio = configParams.fecha_inicio;
      if (configParams.fecha_fin) params.fecha_fin = configParams.fecha_fin;
      if (configParams.estado) params.estado = configParams.estado;
      
      const response = await informesAPI.generar(selectedInforme.id, params);
      setResultados(response.data);
      setIsConfigDialogOpen(false);
      setIsResultsDialogOpen(true);
      
      toast({
        title: "Informe generado",
        description: `Se encontraron ${response.data.total_registros} registros`
      });
    } catch (error) {
      console.error('Error generando informe:', error);
      toast({
        title: "Error",
        description: "No se pudo generar el informe",
        variant: "destructive"
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  const exportToCSV = () => {
    if (!resultados || !resultados.datos.length) return;
    
    const headers = resultados.columnas.join(',');
    const rows = resultados.datos.map(row => 
      resultados.columnas.map(col => {
        const key = col.toLowerCase().replace(/ /g, '_').replace('º', '').replace('€', '');
        const value = row[key] ?? row[Object.keys(row).find(k => k.toLowerCase().includes(col.toLowerCase().split(' ')[0]))] ?? '';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `informe_${selectedInforme?.id}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const tipos = [...new Set(informes.map(i => i.tipo))];
  const subtipos = [...new Set(informes.map(i => i.subtipo))];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Informes</h1>
          <p className="text-gray-600 mt-1">Generación y exportación de informes del despacho</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar informe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {tipos.map(tipo => (
                  <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSubtipo} onValueChange={setFilterSubtipo}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Subtipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los subtipos</SelectItem>
                {subtipos.map(subtipo => (
                  <SelectItem key={subtipo} value={subtipo}>{subtipo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lista de informes */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtipo</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInformes.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      No se encontraron informes
                    </td>
                  </tr>
                ) : (
                  filteredInformes.map((informe, idx) => {
                    const Icon = tipoIcons[informe.tipo] || FileText;
                    return (
                      <tr key={`${informe.id}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tipoColors[informe.tipo]?.split(' ')[0] || 'bg-gray-100'}`}>
                              <Icon className={`w-5 h-5 ${tipoColors[informe.tipo]?.split(' ')[1] || 'text-gray-700'}`} />
                            </div>
                            <span className="font-medium text-gray-900">{informe.nombre}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-gray-600 text-sm max-w-md">
                          {informe.descripcion}
                        </td>
                        <td className="px-4 py-4">
                          <Badge className={`${tipoColors[informe.tipo]} hover:${tipoColors[informe.tipo]}`}>
                            {informe.tipo}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 text-gray-600 text-sm">
                          {informe.subtipo}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => openConfigDialog(informe)}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Generar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {/* Paginación */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-gray-500">
              Items por página: <span className="font-medium">100</span>
            </div>
            <div className="text-sm text-gray-500">
              1-{filteredInformes.length} de {filteredInformes.length}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diálogo de configuración */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Configurar Informe
            </DialogTitle>
          </DialogHeader>
          
          {selectedInforme && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900">{selectedInforme.nombre}</h4>
                <p className="text-sm text-gray-600 mt-1">{selectedInforme.descripcion}</p>
                <div className="flex gap-2 mt-2">
                  <Badge className={tipoColors[selectedInforme.tipo]}>{selectedInforme.tipo}</Badge>
                  <Badge variant="outline">{selectedInforme.subtipo}</Badge>
                </div>
              </div>

              <div className="space-y-4">
                <h5 className="font-medium text-gray-700">Filtros opcionales</h5>
                
                {/* Cliente */}
                {(selectedInforme.subtipo.includes('cliente') || selectedInforme.id === 'personalizado') && (
                  <div>
                    <Label>Cliente</Label>
                    <Select 
                      value={configParams.cliente_id || "all"} 
                      onValueChange={(v) => setConfigParams({...configParams, cliente_id: v === "all" ? "" : v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos los clientes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los clientes</SelectItem>
                        {filtros.clientes?.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.id_interno} - {c.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Responsable */}
                {(selectedInforme.subtipo.includes('responsable') || selectedInforme.id === 'personalizado') && (
                  <div>
                    <Label>Responsable</Label>
                    <Select 
                      value={configParams.responsable_id || "all"} 
                      onValueChange={(v) => setConfigParams({...configParams, responsable_id: v === "all" ? "" : v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos los responsables" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los responsables</SelectItem>
                        {filtros.responsables?.map(r => (
                          <SelectItem key={r.id} value={String(r.id)}>
                            {r.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Rango de fechas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha inicio</Label>
                    <Input
                      type="date"
                      value={configParams.fecha_inicio}
                      onChange={(e) => setConfigParams({...configParams, fecha_inicio: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Fecha fin</Label>
                    <Input
                      type="date"
                      value={configParams.fecha_fin}
                      onChange={(e) => setConfigParams({...configParams, fecha_fin: e.target.value})}
                    />
                  </div>
                </div>

                {/* Estado */}
                {selectedInforme.tipo === 'Expedientes' && (
                  <div>
                    <Label>Estado</Label>
                    <Select 
                      value={configParams.estado || "all"} 
                      onValueChange={(v) => setConfigParams({...configParams, estado: v === "all" ? "" : v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos los estados" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los estados</SelectItem>
                        {filtros.estados_expediente?.map(e => (
                          <SelectItem key={e} value={e}>{e}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleGenerateReport} disabled={generatingReport}>
              {generatingReport ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Generar Informe
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de resultados */}
      <Dialog open={isResultsDialogOpen} onOpenChange={setIsResultsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Resultados: {selectedInforme?.nombre}
              </div>
              <Button size="sm" variant="outline" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {resultados && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Resumen */}
              {resultados.resumen && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {Object.entries(resultados.resumen).map(([key, value]) => (
                    <div key={key} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xl font-bold text-gray-900">
                        {typeof value === 'number' 
                          ? (key.includes('facturado') || key.includes('cobrado') || key.includes('pendiente') || key.includes('valor'))
                            ? `${value.toLocaleString('es-ES')}€`
                            : value.toLocaleString('es-ES')
                          : typeof value === 'object'
                            ? Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ')
                            : value
                        }
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Tabla de resultados */}
              <div className="flex-1 overflow-auto border rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      {resultados.columnas.map((col, idx) => (
                        <th key={idx} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {resultados.datos.length === 0 ? (
                      <tr>
                        <td colSpan={resultados.columnas.length} className="px-4 py-8 text-center text-gray-500">
                          No hay datos para mostrar
                        </td>
                      </tr>
                    ) : (
                      resultados.datos.map((row, rowIdx) => (
                        <tr key={rowIdx} className="hover:bg-gray-50">
                          {Object.values(row).map((value, cellIdx) => (
                            <td key={cellIdx} className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                              {value === null || value === undefined ? '-' : 
                               typeof value === 'number' ? value.toLocaleString('es-ES') :
                               String(value)}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="pt-4 text-sm text-gray-500 text-center">
                Total: {resultados.total_registros} registros
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResultsDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Informes;
